import React, { ChangeEvent, useState } from 'react';

import { useSnackbar } from 'notistack';

import { ExpandLess, ExpandMore, FileDownload } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAutoSetupProperties } from '~/hooks/apis/vendors/useAutoSetupProperties';

import PropertiesUploadResult from './PropertiesUploadResult';

const TEMPLATE_HEADER =
  'Vendor Id,Name,Address,Latitude,Longitude,City,State/Country,Max Child Age,Max Infant Age,Channel Manager,Hotel Code,Channel Supplier Id,Offer Id';

interface Props {
  isModalOpen: boolean;
  closeModal: () => void;
}

export default function PropertiesCsvUploadModal({ isModalOpen, closeModal }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const autoSetupMutation = useAutoSetupProperties();

  const handleClose: DialogProps['onClose'] = () => {
    if (autoSetupMutation.isPending) {
      return;
    }
    if (autoSetupMutation.isSuccess || autoSetupMutation.isError) {
      setShowCloseConfirm(true);
      return;
    }
    closeModal();
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    closeModal();
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob([TEMPLATE_HEADER], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-properties-upload-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
  };

  const handleSubmit = () => {
    if (!file) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' });
      return;
    }
    autoSetupMutation.mutate(file, {
      onSuccess: () => {
        enqueueSnackbar('Properties uploaded successfully', { variant: 'success' });
      },
      onError: (error: Error) => {
        enqueueSnackbar(`Upload failed: ${error.message}`, { variant: 'error' });
      },
    });
  };

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      onTransitionExited={() => {
        setFile(null);
        autoSetupMutation.reset();
      }}
      disableEscapeKeyDown={autoSetupMutation.isPending}
      maxWidth={autoSetupMutation.isSuccess ? 'lg' : 'md'}
      fullWidth
    >
      <DialogTitle>New LPC Properties Upload</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {autoSetupMutation.isPending && (
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              Processing upload, please do not close this page...
            </Alert>
          )}

          {autoSetupMutation.isSuccess && <PropertiesUploadResult result={autoSetupMutation.data.result} />}
          {autoSetupMutation.isError && (
            <Alert severity="error">Upload failed: {autoSetupMutation.error.message}</Alert>
          )}

          {!autoSetupMutation.isSuccess && !autoSetupMutation.isPending && (
            <>
              <Alert severity="info">
                This is for DerbySoft LPC properties only. Other channel managers/offer types are not supported by this
                upload. Max 500 properties per upload.
                <Box>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => setShowDetails((prev) => !prev)}
                    sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.5 }}
                  >
                    What does this tool do?
                    {showDetails ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </Link>
                  <Collapse in={showDetails}>
                    <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                      For each row:
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        <li>Creates the property, or reuses an existing one for the same hotel code/vendor</li>
                        <li>Links it to the given LPC offer</li>
                        <li>Populates the offer content/schedules from the property</li>
                        <li>Triggers missing-product detection</li>
                      </Box>
                      Rows are skipped if the offer isn&apos;t an LPC campaign or is already linked to a property.
                    </Typography>
                  </Collapse>
                </Box>
              </Alert>
              <Stack spacing={1}>
                <Typography variant="body2">
                  Need the template? Download it first, fill in your data, then upload:
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={handleDownloadTemplate}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Download template (.csv)
                </Button>
              </Stack>
              <TextField
                type="file"
                onChange={handleFileChange}
                disabled={autoSetupMutation.isPending}
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: '.csv' }}
                fullWidth
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={() => handleClose({}, 'backdropClick')} disabled={autoSetupMutation.isPending}>
          {autoSetupMutation.isSuccess || autoSetupMutation.isError ? 'Close' : 'Cancel'}
        </Button>
        {!autoSetupMutation.isSuccess && (
          <Button variant="contained" onClick={handleSubmit} disabled={!file || autoSetupMutation.isPending}>
            {autoSetupMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {autoSetupMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        )}
      </DialogActions>

      <Dialog open={showCloseConfirm} onClose={() => setShowCloseConfirm(false)}>
        <DialogTitle>Close upload results?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to close? You won&apos;t be able to see these results again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowCloseConfirm(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
